import React, { useEffect, useState, useMemo, useRef } from 'react';
import { useSelector } from 'react-redux';
import { apiService } from '../../services/apiService';
import VehicleTrackingMap from '../FMCR/VehicleTracking/VehicleTrackingMap';
import { mapApiVehicleToTrackingVehicle } from '../FMCR/VehicleTracking/vehicleTracking.adapter';

const ACTIVE_STATUSES = new Set([
  'new', 'in_transit', 'driver_completed',
  'New', 'InTransit', 'DriverCompleted',
]);

const TripTrackingMap = () => {
  const { tripListsData, selectedTripId } = useSelector((s) => s.trips);
  const [viewAllTrips, setViewAllTrips] = useState([]);
  const [fmcrVehicleMap, setFmcrVehicleMap] = useState({});
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const timerRef = useRef(null);

  const loadData = async () => {
    try {
      const tripsRes = await apiService.viewAllTrips();
      const trips = Array.isArray(tripsRes?.data)
        ? tripsRes.data
        : Array.isArray(tripsRes) ? tripsRes : [];
      setViewAllTrips(trips);

      const vehicleIds = [...new Set(trips.map((t) => t.vehicle_id).filter(Boolean))];
      if (vehicleIds.length > 0) {
        const viewRes = await apiService.getFmcrVehiclesView({ vehicle_Ids: vehicleIds });
        const viewList = Array.isArray(viewRes?.data)
          ? viewRes.data
          : Array.isArray(viewRes) ? viewRes : [];
        const vMap = {};
        viewList.forEach((v) => { if (v.id) vMap[v.id] = v; });
        setFmcrVehicleMap(vMap);
      }
    } catch (_) {}
  };

  useEffect(() => {
    loadData();
    timerRef.current = setInterval(loadData, 30000);
    return () => clearInterval(timerRef.current);
  }, []);

  const vehicles = useMemo(() => {
    if (!viewAllTrips.length) return [];

    const livePos = {};
    if (Array.isArray(tripListsData)) {
      tripListsData.forEach((t) => {
        if (t.id && t.trip_current_detail) livePos[t.id] = t.trip_current_detail;
      });
    }

    return viewAllTrips
      .filter((t) => ACTIVE_STATUSES.has(t.status))
      .map((t) => {
        const fmcrV = fmcrVehicleMap[t.vehicle_id] || {};
        const liveTcd = livePos[t.id];
        const liveHasCoords = liveTcd && Number(liveTcd.lat) !== 0 && Number(liveTcd.lng) !== 0;

        const merged = {
          ...fmcrV,
          id: t.vehicle_id || t.id,
          registration_number:
            t.vehicle?.registration_number || fmcrV.registration_number || '',
          // Prefer FMCR vehicle_type — it carries vehicle_class (HTV/LTV) for correct map icon
          vehicle_type: fmcrV.vehicle_type || t.vehicle?.vehicle_type,
          primary_driver: t.primary_driver || fmcrV.primary_driver,
          polyline: t.polyline,
          start_trip: t.start_trip,
          end_trip: t.end_trip,
          last_log: liveHasCoords
            ? { ...(fmcrV.last_log || {}), lat: liveTcd.lat, lng: liveTcd.lng, speed: liveTcd.speed || 0 }
            : fmcrV.last_log || null,
          // fmcr_trip carries route + trip ID — no trip_current_detail (it has 0,0)
          fmcr_trip: {
            id: t.id,
            trip_id: t.trip_id || t.id,
            polyline: t.polyline,
            start_trip: t.start_trip,
            end_trip: t.end_trip,
            customer: t.customer,
          },
          cust: t.customer?.name || fmcrV.cust || '',
        };

        return mapApiVehicleToTrackingVehicle(merged);
      })
      .filter(Boolean);
  }, [viewAllTrips, fmcrVehicleMap, tripListsData]);

  // When selectedTripId changes, find and highlight the corresponding vehicle on the map
  useEffect(() => {
    if (!selectedTripId || !vehicles.length) {
      setSelectedVehicle(null);
      return;
    }
    const selId = Number(selectedTripId);
    const match = vehicles.find((v) => Number(v.trip?.id) === selId);
    setSelectedVehicle(match || null);
  }, [selectedTripId, vehicles]);

  return (
    // display:flex + flexDirection:column so map-shell's flex:1 fills the column height
    <div style={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column' }}>
      <VehicleTrackingMap
        mode="live"
        vehicles={vehicles}
        selectedVehicle={selectedVehicle}
        onSelectVehicle={setSelectedVehicle}
      />
    </div>
  );
};

export default TripTrackingMap;
